/**
 * 交易详情工具函数
 * 包含状态映射、数据转换等通用逻辑
 */

import {
    TransactionStatus,
    TransactionType,
    TransactionDetailStatus,
    type TransactionDetail,
    type WithdrawMessageInfo,
} from '@/components/transactionDetail/types'
import { formatDate } from '@/utils/date'

// 转换为数字或原始值
export const convertToNumberOrOriginal = (value: string | number | null | undefined): string | number | null | undefined => {
    // 如果原始值是 null 或 undefined，直接返回它本身
    if (value === null || typeof value === 'undefined') {
        return value
    }

    // 尝试将值转换为数字
    const number = Number(value)

    // 检查转换结果是否是有效的数字（不是 NaN）
    if (!isNaN(number)) {
        return number
    }

    // 如果转换失败，返回原始值
    return value
}

// 查询详情接口参数 event
export enum QueryOrderParamsEvent {
    PAYIN = 'payin', // 充值
    PAYOUT = 'payout', // 提现
}

/**
 * 提现状态映射规则类型定义
 */
export interface WithdrawStatusCondition {
    state?: number[]
    errorcode?: number[]
}

export interface WithdrawStatusRule {
    status: TransactionStatus
    condition: WithdrawStatusCondition
}

/**
 * 提现状态映射规则
 */
export const WITHDRAW_STATUS_RULES: {
    withErrorCode: WithdrawStatusRule[]
    withoutErrorCode: WithdrawStatusRule[]
} = {
    // errorcode 存在时的规则
    withErrorCode: [
        { status: TransactionStatus.PROCESSING, condition: { state: [0, 1] } },
        { status: TransactionStatus.SUCCESS, condition: { errorcode: [0] } },
        { status: TransactionStatus.FAILED, condition: { errorcode: [1, 2, 3, 4] } },
    ],

    // errorcode 不存在时的规则
    withoutErrorCode: [
        { status: TransactionStatus.SUCCESS, condition: { state: [3] } },
        { status: TransactionStatus.FAILED, condition: { state: [2, 4, 6] } },
        { status: TransactionStatus.PROCESSING, condition: { state: [0, 1, 5] } },
    ],
}

/**
 * 提现交易消息状态映射规则（多了撤回状态显示）
 */
export const WITHDRAW_MSG_STATUS_RULES: {
    withErrorCode: WithdrawStatusRule[]
    withoutErrorCode: WithdrawStatusRule[]
} = {
    // errorcode 存在时的规则
    withErrorCode: [
        { status: TransactionStatus.PROCESSING, condition: { state: [0, 1] } },
        { status: TransactionStatus.SUCCESS, condition: { errorcode: [0] } },
        { status: TransactionStatus.FAILED, condition: { errorcode: [1, 2, 3] } },
        { status: TransactionStatus.WITHDRAWN, condition: { errorcode: [4] } },
    ],

    // errorcode 不存在时的规则
    withoutErrorCode: [
        { status: TransactionStatus.SUCCESS, condition: { state: [3] } },
        { status: TransactionStatus.FAILED, condition: { state: [2, 4] } },
        { status: TransactionStatus.PROCESSING, condition: { state: [0, 1, 5] } },
        { status: TransactionStatus.WITHDRAWN, condition: { state: [6] } },
    ],
}

/**
 * 提现详细状态映射配置
 */
export interface WithdrawDetailStatusConfig {
    detailStatus: TransactionDetailStatus
    failReason?: string
}

/**
 * 提现详细状态映射表 - 替代复杂的 if/else/switch 逻辑
 */
export const WITHDRAW_DETAIL_STATUS_MAP = {
    // state 为 0 或 1 时的直接映射
    directStateMapping: new Map<number, TransactionDetailStatus>([
        [0, TransactionDetailStatus.WITHDRAW_BACKEND_REVIEWING],
        [1, TransactionDetailStatus.WITHDRAW_BACKEND_REVIEWED],
    ]),

    // 当存在 errorcode 时的映射
    withErrorCodeMapping: new Map<number, WithdrawDetailStatusConfig>([
        [0, { detailStatus: TransactionDetailStatus.WITHDRAW_SUCCESS }],
        [1, { detailStatus: TransactionDetailStatus.WITHDRAW_REJECTED, failReason: 'transaction.bank_fluctuation' }],
        [2, { detailStatus: TransactionDetailStatus.WITHDRAW_REJECTED, failReason: 'transaction.withdrawal_information_incorrect' }],
        [3, { detailStatus: TransactionDetailStatus.WITHDRAW_REJECTED, failReason: 'transaction.order_rejected' }],
        [4, { detailStatus: TransactionDetailStatus.WITHDRAW_WITHDRAWN }],
    ]),

    // 当不存在 errorcode 时的 state 映射
    withoutErrorCodeMapping: new Map<number, WithdrawDetailStatusConfig>([
        [2, { detailStatus: TransactionDetailStatus.WITHDRAW_REJECTED, failReason: 'transaction.bank_fluctuation' }],
        [3, { detailStatus: TransactionDetailStatus.WITHDRAW_SUCCESS }],
        [4, { detailStatus: TransactionDetailStatus.WITHDRAW_REJECTED, failReason: 'transaction.order_rejected' }],
        [5, { detailStatus: TransactionDetailStatus.WITHDRAW_BACKEND_REVIEWING }],
        [6, { detailStatus: TransactionDetailStatus.WITHDRAW_WITHDRAWN }],
    ]),

    // 默认配置
    defaultConfig: { detailStatus: TransactionDetailStatus.WITHDRAW_BACKEND_REVIEWED, failReason: '' },
}

/**
 * 获取提现详细状态 - 配置驱动方式
 */
export const getWithdrawDetailStatus = (state: number, errorcode: any): WithdrawDetailStatusConfig => {
    errorcode = convertToNumberOrOriginal(errorcode)
    // 1. 优先检查直接状态映射（state 0, 1）
    if (WITHDRAW_DETAIL_STATUS_MAP.directStateMapping.has(state)) {
        return {
            detailStatus: WITHDRAW_DETAIL_STATUS_MAP.directStateMapping.get(state)!,
            failReason: '',
        }
    }

    // 2. 检查 errorcode 映射
    if (errorcode !== undefined && errorcode !== null) {
        const config = WITHDRAW_DETAIL_STATUS_MAP.withErrorCodeMapping.get(errorcode)
        if (config) {
            return config
        }
    }

    // 3. 检查无 errorcode 时的 state 映射
    const config = WITHDRAW_DETAIL_STATUS_MAP.withoutErrorCodeMapping.get(state)
    if (config) {
        return config
    }

    // 4. 返回默认配置
    return WITHDRAW_DETAIL_STATUS_MAP.defaultConfig
}

/**
 * 获取充值订单状态 （充值只有成功、进行中）
 */
export const getDepositStatus = (isFinish: boolean): TransactionStatus => {
    return isFinish === true ? TransactionStatus.SUCCESS : TransactionStatus.PROCESSING
}

/**
 * 根据 errorcode 和 state 获取提现状态
 */
export const getWithdrawStatus = (errorcode: any, state: number): TransactionStatus => {
    errorcode = convertToNumberOrOriginal(errorcode)
    const hasErrorCode = errorcode !== undefined && errorcode !== null
    const rules = hasErrorCode ? WITHDRAW_STATUS_RULES.withErrorCode : WITHDRAW_STATUS_RULES.withoutErrorCode

    for (const rule of rules) {
        const { status, condition } = rule

        if (hasErrorCode) {
            // 有 errorcode：优先检查 state，然后检查 errorcode
            if (condition.state && condition.state.includes(state)) {
                return status
            }
            if (condition.errorcode && condition.errorcode.includes(errorcode as number)) {
                return status
            }
        } else {
            // 无 errorcode：只检查 state
            if (condition.state && condition.state.includes(state)) {
                return status
            }
        }
    }

    return TransactionStatus.PROCESSING // 默认值
}

/**
 * 根据 errorcode 和 state 获取提现交易消息状态
 */
/**
 * 提现消息的标题和内容映射
 */
export const WITHDRAW_MESSAGE_INFO_MAP: ReadonlyMap<TransactionStatus, WithdrawMessageInfo> = new Map([
    [
        TransactionStatus.SUCCESS,
        {
            title: 'transaction.withdrawal_completed',
            content: 'transaction.withdraw_successful',
        },
    ],
    [
        TransactionStatus.PROCESSING,
        {
            title: 'transaction.withdrawal_in_progress',
            content: 'transaction.withdraw_processing',
        },
    ],
    [
        TransactionStatus.WITHDRAWN,
        {
            title: 'transaction.withdrawal_cancelled',
            content: 'transaction.withdraw_cancelled',
        },
    ],
    [
        TransactionStatus.FAILED,
        {
            title: 'transaction.withdrawal_failed',
            content: 'transaction.withdraw_failed',
        },
    ],
])

export const getWithdrawMsgStatus = (errorcode: any, state: number): TransactionStatus => {
    errorcode = convertToNumberOrOriginal(errorcode)
    const hasErrorCode = errorcode !== undefined && errorcode !== null
    const rules = hasErrorCode ? WITHDRAW_MSG_STATUS_RULES.withErrorCode : WITHDRAW_MSG_STATUS_RULES.withoutErrorCode

    for (const rule of rules) {
        const { status, condition } = rule

        if (hasErrorCode) {
            // 有 errorcode：优先检查 state，然后检查 errorcode
            if (condition.state && condition.state.includes(state)) {
                return status
            }
            if (condition.errorcode && condition.errorcode.includes(errorcode as number)) {
                return status
            }
        } else {
            // 无 errorcode：只检查 state
            if (condition.state && condition.state.includes(state)) {
                return status
            }
        }
    }

    // 默认返回进行中状态的信息
    return TransactionStatus.PROCESSING
}

/**
 * 提现详情数据转换 TransactionDetail 格式
 */
export const convertWithdrawDataToDetail = (content: any[]): TransactionDetail => {
    let source = content[0]

    // 状态映射：0=未审核（订单已创建）, 1=已审核订单正在提现（银行处理中）
    // state 判断 0/1 之后，其他状态通过 errorCode 判断
    // errorcode 0-提现成功，1-银行波动、2-提款信息错误、3-客服决绝（1、2、3 都表示提现未通过），4-用户取消（提现撤回），（1、2、3、4 都表示失败状态）
    // 除了提现成功、失败，其他都是进行中状态，也就是 status 对应的状态值
    // 如果 errorcode 字段不存在，就用 state 字段判断
    // detailStatus 对应更加细化的状态（0/1，0，1，2，3，4）

    // 使用配置驱动的方式获取状态信息
    const status = getWithdrawStatus(source.errorcode, source.state)
    const { detailStatus, failReason } = getWithdrawDetailStatus(source.state, source.errorcode)

    const transactionData: TransactionDetail = {
        id: source.orderid || '',
        time: source.creatat || '',
        type: TransactionType.WITHDRAW,
        curSymbol: '',
        amount: source.amount || 0,
        currency: source.currency,
        status: status,
        detailStatus: detailStatus,
        fee: source.fee,
        paymentChannel: source.channel,
        failReason: failReason || '',
        finishat: source.checktime,
    }

    return transactionData
}

// 充值详情数据转换 TransactionDetail 格式
export const convertDepositDataToDetail = (content: any[]): TransactionDetail | null => {
    // 1. 分类获取不同类型的数据
    const createorder = content.find((item) => item?.message === 'createorder')
    const orderspeedup = content.find((item) => item?.message === 'orderspeedup')
    const orderfinish = content.find((item) => item?.message === 'orderfinish')

    if (orderfinish && !createorder) {
        let orderData = orderfinish.context
        let orderPur = orderfinish.context.purchase

        const transactionData: TransactionDetail = {
            id: orderData.id,
            time: orderPur.createat || '',
            type: TransactionType.DEPOSIT,
            amount: orderPur.item.cny || 0,
            curSymbol: '',
            currency: orderPur.item.currency || '',

            // 状态判断
            status: TransactionStatus.SUCCESS,
            detailStatus: TransactionDetailStatus.DEPOSIT_SUCCESS,

            // 其他信息
            fee: 0,
            description: '',
            paymentChannel: orderfinish.channelName || '',
            payurl: '',

            // 完成状态特有信息 - 只有orderfinish才有的数据
            before: orderData.before !== undefined ? orderData.before : -1,
            after: orderData.after !== undefined ? orderData.after : -1,
            finishat: formatDate(orderData.finishat || orderfinish.time || ''),

            // 奖励信息
            gold: orderPur.item.gifts?.gold || 0,

            // 加速时间 - orderspeedup状态特有
            at: '',
        }

        return transactionData
    }
    // 数据完整性检查
    if (!createorder && !orderspeedup && !orderfinish) {
        return null
    }

    // 2. 确定订单当前状态和主要数据源
    let orderStatus: 'created' | 'speedup' | 'finished'
    let primaryData: any
    let baseData: any // 用于获取基础信息的数据源（createorder）
    let paymentChannel = createorder.channelName || ''

    if (orderfinish) {
        orderStatus = 'finished'
        primaryData = orderfinish
        baseData = createorder || orderfinish // 优先使用createorder获取基础信息
    } else if (orderspeedup) {
        orderStatus = 'speedup'
        primaryData = orderspeedup
        baseData = createorder || orderspeedup // orderspeedup数据不完整，优先用createorder
    } else {
        orderStatus = 'created'
        primaryData = createorder || content[0]
        baseData = primaryData
    }

    // 数据源验证
    if (!primaryData || !baseData) {
        return null
    }

    // 3. 智能数据提取 - 优先从完整数据源获取信息
    const extractOrderData = () => {
        // 基础订单信息 - 优先从baseData获取
        const baseContext = baseData?.context || {}
        const primaryContext = primaryData?.context || {}

        // 商品信息 - 根据数据类型智能选择
        let item: any
        if (orderStatus === 'finished' && primaryContext.purchase?.item) {
            item = primaryContext.purchase.item
        } else if (baseContext.item) {
            item = baseContext.item
        } else if (primaryContext.item) {
            item = primaryContext.item
        } else {
            item = {}
        }

        // 订单ID - 优先从完整数据获取
        const orderId = baseContext.id || primaryContext.id || ''

        // 创建时间 - 根据状态智能选择
        let createTime = ''
        if (orderStatus === 'finished' && primaryContext.purchase?.createat) {
            createTime = primaryContext.purchase.createat
        } else if (baseContext.createat) {
            createTime = baseContext.createat
        } else if (primaryContext.createat) {
            createTime = primaryContext.createat
        } else if (primaryData?.time) {
            createTime = primaryData.time
        }

        // 过期时间判断 - 只对非完成状态检查
        let isExpired = false
        if (orderStatus !== 'finished') {
            let expireTime = baseContext.expireat || primaryContext.expireat

            // 如果没有过期时间字段，使用创建时间 + 3分钟作为过期时间
            if (!expireTime && createTime) {
                const createDate = new Date(createTime)
                if (!isNaN(createDate.getTime())) {
                    // 创建时间 + 3分钟
                    const expireDate = new Date(createDate.getTime() + 3 * 60 * 1000)
                    expireTime = expireDate.toISOString()
                }
            }

            if (expireTime) {
                isExpired = new Date(expireTime) < new Date()
            }
        }

        // 支付渠道
        // let paymentChannel = ''
        // if (orderStatus === 'finished' && primaryContext.channel) {
        //     paymentChannel = primaryContext.channel
        // } else if (baseContext.type) {
        //     paymentChannel = baseContext.type
        // } else if (primaryContext.type) {
        //     paymentChannel = primaryContext.type
        // }

        // 支付URL - 优先从baseData获取（createorder中更完整）
        const payurl = baseContext.payurl || primaryContext.payurl || ''

        return {
            item,
            orderId,
            createTime,
            isExpired,
            // paymentChannel,
            payurl,
            baseContext,
            primaryContext,
        }
    }

    const orderData = extractOrderData()

    // 4. 构建最终的交易详情数据
    const transactionData: TransactionDetail = {
        id: orderData.orderId,
        time: orderData.createTime,
        type: TransactionType.DEPOSIT,
        amount: orderData.item.cny || 0,
        curSymbol: '',
        currency: orderData.item.currency || '',

        // 状态判断
        status: getDepositStatus(orderStatus === 'finished'),
        detailStatus:
            orderStatus === 'finished'
                ? TransactionDetailStatus.DEPOSIT_SUCCESS
                : orderData.isExpired
                ? TransactionDetailStatus.DEPOSIT_ORDER_CLOSED
                : TransactionDetailStatus.DEPOSIT_PROCESSING,

        // 其他信息
        fee: 0,
        description: '',
        paymentChannel: paymentChannel,
        payurl: orderData.payurl,

        // 完成状态特有信息 - 只有orderfinish才有的数据
        before: orderStatus === 'finished' ? (orderData.primaryContext.before !== undefined ? orderData.primaryContext.before : -1) : -1,
        after: orderStatus === 'finished' ? (orderData.primaryContext.after !== undefined ? orderData.primaryContext.after : -1) : -1,
        finishat: orderStatus === 'finished' ? formatDate(orderData.primaryContext.finishat || baseData.time || '') : '',

        // 奖励信息
        gold: orderData.item.gifts?.gold || 0,

        // 加速时间 - orderspeedup状态特有
        at: orderspeedup ? formatDate(orderspeedup.context.at || '', 'YYYY/MM/DD HH:mm:ss') : '',
    }

    return transactionData
}

/**
 * 从交易消息中提取标准化的交易信息
 * 处理充值和提现消息的数据结构差异
 */
export const extractTransactionInfoFromMessage = (rawMessage: any) => {
    const isPayin = rawMessage.event === 'payin'
    const isPayout = rawMessage.event === 'payout'
    const context = rawMessage.content?.context

    if (!context) {
        return null
    }

    if (isPayin) {
        // 充值消息数据提取
        return {
            orderId: context.id || '',
            amount: context.item?.cny || 0,
            currency: context.item?.currency || '',
            createTime: context.createat || '',
            channel: context.channel || '',
            type: TransactionType.DEPOSIT,
            status: getDepositStatus(context.state === 'finish'),
            payurl: context.payurl || '',
            gold: context.item?.worth?.gold || 0,
            // 原始数据保留
            rawState: context.state,
            rawContext: context,
        }
    } else if (isPayout) {
        // 提现消息数据提取
        return {
            orderId: context.orderid || '',
            amount: context.amount || 0,
            currency: context.currency || '',
            createTime: context.creatat || '',
            channel: context.channel || '',
            type: TransactionType.WITHDRAW,
            status: getWithdrawMsgStatus(context.errorcode, context.state),
            fee: context.fee || 0,
            gold: context.gold || 0,
            paytype: context.paytype || '',
            // 原始数据保留
            rawState: context.state,
            rawErrorCode: context.errorcode,
            rawContext: context,
        }
    }

    return null
}

/**
 * 获取消息的正确时间字段
 * 处理充值和提现时间字段命名差异
 */
export const getMessageTime = (rawMessage: any): string => {
    const context = rawMessage.content?.context
    if (!context) return ''

    if (rawMessage.event === 'payin') {
        return context.createat || context.timestamp || ''
    } else if (rawMessage.event === 'payout') {
        return context.creatat || ''
    }

    return ''
}

/**
 * 判断当前货币是否为 USDT
 * @param currency 货币类型
 * @returns 是否为 USDT
 */
export const isUSDTCurrency = (currency?: string): boolean => {
    return currency === 'USDT'
}

/**
 * 复制到剪贴板
 */
export const copyToClipboard = (text: string): Promise<void> => {
    // 优先使用现代 Clipboard API
    if (navigator.clipboard && window.isSecureContext) {
        return navigator.clipboard.writeText(text)
    }

    // 降级方案：使用传统的 execCommand
    return new Promise<void>((resolve, reject) => {
        const textArea = document.createElement('textarea')
        textArea.value = text
        textArea.style.position = 'fixed'
        textArea.style.left = '-999999px'
        textArea.style.top = '-999999px'
        document.body.appendChild(textArea)
        textArea.focus()
        textArea.select()

        try {
            const result = document.execCommand('copy')
            if (result) {
                resolve()
            } else {
                reject(new Error('Copy failed'))
            }
        } catch (err) {
            reject(err)
        } finally {
            // 确保移除元素
            if (document.body.contains(textArea)) {
                document.body.removeChild(textArea)
            }
        }
    })
}

export function checkPwaSupport() {
    const support = {
        serviceWorker: 'serviceWorker' in navigator,
        webAppManifest:
            'HTMLLinkElement' in window && 'relList' in HTMLLinkElement.prototype && document.createElement('link').relList.supports('manifest'),
        pushNotifications: 'Notification' in window && 'PushManager' in window,
        backgroundSync: 'serviceWorker' in navigator && 'SyncManager' in window,
        installPrompt: false, // 需要通过事件监听检测
    }

    // 监听安装提示事件
    window.addEventListener('beforeinstallprompt', (e) => {
        support.installPrompt = true
        e.preventDefault()
        window.deferredPrompt = e
    })

    return support
}
